import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/hooks';

import {
    MdCheckCircle,
    MdEmail,
    MdOutlineError,
    MdSend,
    MdVisibility,
} from 'react-icons/md';
import Card from 'components/card';
import {
    addTransaction,
    downloadInvoice,
    generateCreditNote,
    exportInvoices,
    voidCreditNote,
} from 'interfaces/invoice';
import CreateButton from 'components/buttons/CreateButton';
import InvoiceMenu from './invoiceMenu';
import ExtraMenu from './extraMenu';
import invoiceImg from 'assets/img/invoice.png';
import SearchIcon from 'components/icons/SearchIcon';
import Select from 'components/fields/Select';
import CreditNoteModal from './CreditNoteModal';
import MarkAsPaidModal from './MarkAsPaidModal';
import GoBack from 'components/actions/GoBack';
import EmailsHistoryModal from './emailsHistoryModal';
import CreditNotePartialModal from './CreditNotePartialModal';
import PaiementExportModal from './PaiementExportModal';
import SimpleInvoiceExportModal from './SimpleInvoiceExportModal';
import { getCompanyAccounts } from 'interfaces/user';
import VoidCreditNoteModal from './VoidCreditNoteModal';
import InvoicePdfPreviewModal from './InvoicePdfPreviewModal';

const columnHelper = createColumnHelper<any>();

const CreditNoteAmount = React.memo(({ transactions }: { transactions: any[] }) => {
    const totalCreditNote = transactions
        .filter((t: any) => t?.paymentMethod === 'credit-note')
        .reduce((acc: any, t: any) => acc + (t?.amount || 0), 0);
    if (totalCreditNote <= 0) return null;
    return (
        <p className="text-xs text-toola-500">
            -{totalCreditNote.toFixed(2)}€ d'avoir
        </p>
    );
});

const TooPerceiveAmount = React.memo(({ transactions }: { transactions: any[] }) => {
    const totalTooPerceive = transactions
        .filter((t: any) => t?.paymentMethod === 'too-perceive')
        .reduce((acc: any, t: any) => acc + (t?.amount || 0), 0);
    const hasTooPerceiveReference = transactions.some((t: any) =>
        t?.reference?.toLowerCase().includes('trop perçu'),
    );
    if (totalTooPerceive <= 0) return null;
    return (
        <p className="text-xs text-toola-500">
            -{totalTooPerceive.toFixed(2)}€ {hasTooPerceiveReference ? 'de trop perçu' : "d'avoir restant"}
        </p>
    );
});
export default function InvoicesTable(props: {
    tableData: any;
    onOpen: any;
    onEmailSenderModalOpen: any;
    invoicesMeta: any;
    onFilter: any;
    onRefresh: any;
    onCreateCreditNote: any;
    search: string;
}) {
    const {
        onOpen,
        onEmailSenderModalOpen,
        invoicesMeta,
        onFilter,
        onRefresh,
        onCreateCreditNote,
        search,
    } = props;

    const params = useParams();
    const { isOpen, onOpen: onOpenCreditNote, onClose } = useDisclosure();
    const {
        isOpen: isOpenCreditNotePartial,
        onOpen: onOpenCreditNotePartial,
        onClose: onCloseCreditNotePartial,
    } = useDisclosure();

    const {
        isOpen: isOpenMarkAsPaid,
        onOpen: onOpenMarkAsPaid,
        onClose: OnCloseMarkAsPaid,
    } = useDisclosure();

    const {
        isOpen: isOpenEmailsHistory,
        onOpen: onOpenEmailsHistory,
        onClose: OnCloseEmailHistory,
    } = useDisclosure();

    const {
        isOpen: isPaiementExportModalOpen,
        onOpen: onPaiementExportModalOpen,
        onClose: onPaiementExportModalClose,
    } = useDisclosure();

    const {
        isOpen: isSimpleExportModalOpen,
        onOpen: onSimpleExportModalOpen,
        onClose: onSimpleExportModalClose,
    } = useDisclosure();

    const {
        isOpen: isVoidCreditNoteModalOpen,
        onOpen: onVoidCreditNoteModalOpen,
        onClose: onVoidCreditNoteModalClose,
    } = useDisclosure();

    const {
        isOpen: isPdfPreviewOpen,
        onOpen: onPdfPreviewOpen,
        onClose: onPdfPreviewClose,
    } = useDisclosure();

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [status, setStatus] = React.useState('all');
    const [paiementMethod, setPaymentMethod] = React.useState('all');
    const [account, setAccount] = React.useState('all');
    const [data, setData] = React.useState<any[]>([]);

    // Data saved for modals
    const [creditNoteToCreate, setCreditNoteToCreate] = useState(null);
    const [invoiceToMarkAsPaid, setInvoiceToMarkAsPaid] = useState(null);
    const [invoiceToVoid, setInvoiceToVoid] = useState<any>(null);
    const [accounts, setAccounts] = useState<any[]>([]);
    
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const [previewInvoiceBlob, setPreviewInvoiceBlob] = useState<Blob | null>(null);
    const [previewFileName, setPreviewFileName] = useState<string>('');
    const previewBlobUrlRef = useRef<string | null>(null);

    const nav = useNavigate();
    
    useEffect(() => {
        onFilter(search, status, paiementMethod, account);
    }, [status, paiementMethod, search, account]);

    const initAccounts = async () => {
        const res = await getCompanyAccounts();
        if (res) {
            setAccounts(res.data);
        }
    };

    useEffect(() => {
        const tableData = props.tableData.map((invoice: any) => {
            const date = invoice.invoicedAt || invoice.updatedAt;
            return {
                ...invoice,
                customer: invoice.customer.company,
                customerId: invoice.customer.id,
                n: {
                    invoiceNumber: invoice.invoiceNumber,
                    date: date.split('T')[0].split('-').reverse().join('/'),
                    fullName: invoice.createdBy?.fullName
                },
                title: `${invoice.invoiceLines.length} commandes`,
            };
        });
        setData(tableData);
        initAccounts();
    }, [props.tableData]);

    // const downloadAllInvoices = async () => {
    //     try {
    //         const response = await fetch('/api/invoices/download-all-invoices', {
    //             method: 'GET',
    //             headers: {
    //                 Authorization: `Bearer ${localStorage.getItem('token')}`, // Si vous utilisez un token d'authentification
    //             },
    //         });
    
    //         if (!response.ok) {
    //             throw new Error('Erreur lors du téléchargement des factures.');
    //         }
    
    //         const blob = await response.blob();
    //         const url = window.URL.createObjectURL(blob);
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.setAttribute('download', 'invoices.zip');
    //         document.body.appendChild(link);
    //         link.click();
    //         link.parentNode.removeChild(link);
    //     } catch (error) {
    //         console.error('Erreur:', error);
    //         alert('Impossible de télécharger les factures.');
    //     }
    // };

    const openInvoicePreview = async (id: string) => {
        const invoice = data.find((invoice: any) => invoice._id === id);
        setPreviewFileName(`${invoice?.customer || 'facture'}_${invoice?.invoiceNumber || id}.pdf`);
        setPreviewInvoiceBlob(null);
        setPreviewPdfUrl(null);
        onPdfPreviewOpen();
        try {
            const blob = await downloadInvoice(id);
            if (!blob) return;
            const url = window.URL.createObjectURL(blob);
            if (previewBlobUrlRef.current) {
                window.URL.revokeObjectURL(previewBlobUrlRef.current);
            }
            previewBlobUrlRef.current = url;
            setPreviewInvoiceBlob(blob);
            setPreviewPdfUrl(url);
        } catch (error) {
            console.error('Error generating PDF preview:', error);
        }
    };

    const closeInvoicePreview = () => {
        onPdfPreviewClose();
        if (previewBlobUrlRef.current) {
            window.URL.revokeObjectURL(previewBlobUrlRef.current);
            previewBlobUrlRef.current = null;
        }
        setPreviewPdfUrl(null);
        setPreviewInvoiceBlob(null);
    };

    const downloadPreviewedInvoice = () => {
        if (!previewInvoiceBlob) return;
        const url = window.URL.createObjectURL(previewInvoiceBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = previewFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const addInvoiceTransaction = async (
        invoiceId: string,
        transaction: any,
    ) => {
        const res = await addTransaction(invoiceId, transaction);
        if (!res || !res.data) return;
            //         alert('Unable to download invoices.');
        onRefresh();
    };

    const isOpenEmailsHistoryModal = (invoice: any) => {
        setInvoiceToMarkAsPaid(invoice);
        onOpenEmailsHistory();
    };

    const openTransactionModal = async (selectedInvoice: any) => {
        setInvoiceToMarkAsPaid(selectedInvoice);
        onOpenMarkAsPaid();
    };

    const openCreditNoteModal = async (invoiceId: string) => {
        setCreditNoteToCreate(invoiceId);
        onOpenCreditNote();
    };

    const openVoidCreditNoteModal = (invoice: any) => {
        setInvoiceToVoid(invoice);
        onVoidCreditNoteModalOpen();
    };

    const handleVoidCreditNote = async (invoiceId: string) => {
        const res = await voidCreditNote(invoiceId);
        if (res) onRefresh();
    };

    const createCreditNote = async (
        creditNoteAmount: number = null,
        vat: number = null,
    ) => {
        const res = await generateCreditNote(
            creditNoteToCreate,
            creditNoteAmount,
            vat,
        );
        if (!res) return;
        setCreditNoteToCreate(null);
        onClose();
        onCloseCreditNotePartial();

        // Used to update the table data
        onFilter(null, 'all', 'all', 'all');
    };

    const toggleInvoiceSelection = useCallback((id: string) => {
        setSelectedInvoices((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        );
    }, []);

    const toggleAllInvoices = useCallback(() => {
        setSelectedInvoices((prev) =>
            prev.length === data.length ? [] : data.map((i: any) => i._id),
        );
    }, [data]);

    const handleExportInvoices = async () => {
        if (selectedInvoices.length === 0) return;
        setIsDownloading(true);
        try {
            const blob = await exportInvoices(selectedInvoices);
            if (blob) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `factures-${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsDownloading(false);
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('select', {
            id: 'select',
            header: () => (
                <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-toola-600 focus:ring-toola-500"
                    checked={data.length > 0 && selectedInvoices.length === data.length}
                    onChange={toggleAllInvoices}
                />
            ),
            cell: (info) => (
                <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-toola-600 focus:ring-toola-500"
                    checked={selectedInvoices.includes(info.row.original._id)}
                    onChange={() => toggleInvoiceSelection(info.row.original._id)}
                />
            ),
        }),
        columnHelper.accessor('n', {
            id: 'n',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    N° Facture
                </p>
            ),
            cell: (info) => (
                <>
                    <p className="text-sm text-navy-700 dark:text-white">
                        #{info.getValue()['invoiceNumber']}
                    </p>
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {info.getValue()['date']}
                    </p>
                    {info.getValue()['fullName'] && <p className="text-navy-700 text-xs">
                        Émise par{' '}
                         {info.getValue()['fullName'] || ''}
                    </p>}
                </>
            ),
        }),
        columnHelper.accessor('customer', {
            id: 'customer',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Client
                </p>
            ),
            cell: (info) => (
                <div
                    className="flex gap-1 hover:cursor-pointer hover:underline"
                    onClick={() =>
                        window.open(
                            `/customer/${info.row.original.customerId}`,
                            '_blank',
                        )
                    }
                >
                    <p className="text-md text-toola-500 dark:text-white">
                        {info.getValue()}
                    </p>
                </div>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Statut
                </p>
            ),
            cell: (info) => (
                <div className="flex items-center">
                    {info.getValue() === 'draft' && (
                        <>
                            <MdOutlineError className="text-amber-500 me-1 dark:text-amber-300" />
                            <p className="text-sm font-bold text-navy-700 dark:text-white">
                                Brouillon
                            </p>
                        </>
                    )}
                    {info.getValue() === 'generated' && (
                        <div className="flex bg-red-400 rounded-xl p-1 px-3 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <p className="text-xs font-bold text-white dark:text-white">
                                Non envoyée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'sent' && (
                        <div className="flex bg-amber-500 rounded-xl p-1 px-3 gap-1">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <MdEmail className="text-white me-1 dark:text-white" />
                            <p className="text-xs font-bold text-white">
                                Envoyée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'paid' && (
                        <div className="flex bg-green-500 rounded-xl p-1 px-3 gap-1">
                            <MdCheckCircle className=" text-white" />
                            <p className="text-xs font-bold text-white dark:text-white">
                                Payée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'canceled' && (
                        <div className="flex bg-gray-300 rounded-xl p-1 px-3 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <p className="text-xs font-bold text-white dark:text-white">
                                Annulée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'credit-note' && (
                        <div className="flex bg-blue-400 rounded-xl p-1 px-3 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-toola-300" /> */}
                            <p className="text-xs font-bold text-white">
                                Facture d'avoir
                            </p>
                        </div>
                    )}
                    {/* <MdCancel className="text-red-500 me-1 dark:text-red-300" /> */}
                </div>
            ),
        }),
        columnHelper.accessor('leftToPay', {
            id: 'leftToPay',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Reste à payer
                </p>
            ),
            cell: (info) => (
                <div>
                    {info.getValue() === 0 && (
                        <p className="text-green-500 text-sm font-bold dark:text-white">
                            {info.getValue() >= 0
                                ? info.getValue().toFixed(2)
                                : '-- --'}{' '}
                            €
                        </p>
                    )}
                    {info.getValue() < 0 && (
                        <p className="text-navy-700 text-sm font-bold dark:text-white">
                            {info.getValue() < 0
                                ? info.getValue().toFixed(2)
                                : '-- --'}{' '}
                            €
                        </p>
                    )}
                    {info.getValue() > 0 && (
                        <p className="text-red-500 text-sm font-bold dark:text-white">
                            {info.getValue() >= 0
                                ? info.getValue().toFixed(2)
                                : '-- --'}{' '}
                            €
                        </p>
                    )}
                    <CreditNoteAmount
                        transactions={info.row.original.transactions}
                    />
                    <TooPerceiveAmount
                        transactions={info.row.original.transactions}
                    />
                </div>
            ),
        }),
        columnHelper.accessor('totalHT', {
            id: 'totalHT',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total HT
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('totalTTC', {
            id: 'totalTTC',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total TTC
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('_id', {
            id: '_id',
            header: () => {},
            cell: (info) => (
                <div className="flex items-center gap-5">
                    <MdVisibility
                        title="Prévisualiser la facture"
                        className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                        onClick={() => openInvoicePreview(info.getValue())}
                    />
                    <MdSend
                        title="Envoyer par mail"
                        className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                        onClick={() => onEmailSenderModalOpen(info.getValue())}
                    />
                    <InvoiceMenu
                        disabled={['canceled'].includes(info.row.original.status)}
                        isLastItem={
                            info.row.index === data.length - 1 ||
                            info.row.index === data.length - 2
                        }
                        invoice={info.row.original}
                        openHistory={isOpenEmailsHistoryModal}
                        updateInvoiceStatus={openTransactionModal}
                        createCreditNote={(id: string) =>
                            openCreditNoteModal(id)
                        }
                        voidCreditNote={openVoidCreditNoteModal}
                    />
                </div>
            ),
        }),
    ], [data, selectedInvoices, toggleAllInvoices, toggleInvoiceSelection, openInvoicePreview, onEmailSenderModalOpen, openCreditNoteModal, isOpenEmailsHistoryModal, openTransactionModal]); // eslint-disable-line react-hooks/exhaustive-deps
    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const SendMassInvoice = React.memo(() => {
        return (
            <Card
                extra={
                    'w-full pb-[10px] mb-3 pt-[10px] px-[29px] h-fit bg-blue-200 shadow shadow-3xl border'
                }
            >
                {/* Header */}
                <div className="mt-1">
                    <p className="text-xl font-semibold text-navy-700 dark:text-white">
                        Envoyer vos factures ✉️
                    </p>
                    <p className="mt-3 mb-3 text-base text-gray-600">
                        Toutes les factures non-envoyées de toutes les périodes
                        confondues seront envoyées. (
                        <b className="text-gray-700">
                            {invoicesMeta && invoicesMeta.totalGenerated}{' '}
                            facture
                            {invoicesMeta &&
                                invoicesMeta.totalGenerated > 1 &&
                                's'}
                        </b>{' '}
                        )
                    </p>
                    <CreateButton
                        value={`Envoyer`}
                        extra="my-auto h-10"
                        onClick={() => onEmailSenderModalOpen('all')}
                    />
                    {/* <CreateButton
                        value="Télécharger toutes les factures"
                        extra="my-auto h-10"
                        onClick={downloadAllInvoices}
                    /> */}
                </div>
            </Card>
        );
    });

    return (
        <>
            {invoicesMeta && invoicesMeta.totalGenerated > 0 && (
                <SendMassInvoice />
            )}
            <CreditNoteModal
                isOpen={isOpen}
                onClose={onClose}
                generateCreditNote={createCreditNote}
                generatePartialCreditNote={() => {
                    onClose();
                    onOpenCreditNotePartial();
                }}
            />
            <PaiementExportModal
                isOpen={isPaiementExportModalOpen}
                onClose={onPaiementExportModalClose}
                handleExport={() => {}}
            />
            <SimpleInvoiceExportModal
                isOpen={isSimpleExportModalOpen}
                onClose={onSimpleExportModalClose}
                handleExport={() => {}}
            />
            <CreditNotePartialModal
                isOpen={isOpenCreditNotePartial}
                onClose={onCloseCreditNotePartial}
                generateCreditNote={(amount: number, vat: number) => {
                    createCreditNote(amount, vat);
                }}
            />
            <MarkAsPaidModal
                isOpen={isOpenMarkAsPaid}
                onClose={() => {
                    OnCloseMarkAsPaid();
                    onRefresh();
                }}
                invoice={invoiceToMarkAsPaid}
                onClick={addInvoiceTransaction}
            />
            <EmailsHistoryModal
                isOpen={isOpenEmailsHistory}
                onClose={() => {
                    OnCloseEmailHistory();
                    onRefresh();
                }}
                invoice={invoiceToMarkAsPaid}
                onClick={OnCloseEmailHistory}
            />
            <VoidCreditNoteModal
                isOpen={isVoidCreditNoteModalOpen}
                onClose={onVoidCreditNoteModalClose}
                invoice={invoiceToVoid}
                onConfirm={handleVoidCreditNote}
            />
            <InvoicePdfPreviewModal
                isOpen={isPdfPreviewOpen}
                onClose={closeInvoicePreview}
                pdfUrl={previewPdfUrl}
                fileName={previewFileName}
                onDownload={downloadPreviewedInvoice}
            />
            <GoBack />
            <Card extra={'mt-1 w-full px-6 pb-6 sm:overflow-x-auto border'}>
                <div className="relative flex items-center justify-between pt-4">
                    <div className="text-xl font-bold text-navy-700 dark:text-white">
                        Vos dernières factures
                    </div>
                    <div className="flex items-center gap-4">
                        {selectedInvoices.length > 0 && (
                            <div
                                className={`flex items-center text-sm ${
                                    !isDownloading
                                        ? 'hover:cursor-pointer hover:bg-toola-600'
                                        : 'cursor-wait opacity-70'
                                } bg-none text-white bg-toola-500 py-2 px-3 linear justify-center rounded-xl transition duration-200 h-10`}
                                onClick={
                                    !isDownloading
                                        ? handleExportInvoices
                                        : undefined
                                }
                            >
                                <p className="whitespace-nowrap">
                                    {isDownloading
                                        ? 'Téléchargement...'
                                        : `Télécharger (${selectedInvoices.length})`}
                                </p>
                            </div>
                        )}
                        <CreateButton
                            value="Facturer mes clients"
                            extra="my-auto h-10"
                            onClick={() => nav('/generate-invoice')}
                        />
                        <ExtraMenu
                            onOpen={onOpen}
                            onCreateCreditNote={onCreateCreditNote}
                            onPaiementExportModalOpen={
                                onPaiementExportModalOpen
                            }
                            onSimpleExportModalOpen={onSimpleExportModalOpen}
                        />
                    </div>
                </div>
                <p
                    className="ml-1 text-sm text-toola-500 hover:underline hover:cursor-pointer"
                    onClick={() =>
                        nav(`/unpaid-manager/${params.month}/${params.year}`)
                    }
                >
                    Gestionnaire des impayés
                </p>

                {/* Filters */}
                <div className="mt-2 grid grid-cols-1 xl:grid-cols-11">
                    <div className="xl:mt-4 mt-1 col-span-3">
                        <div className="flex h-[30px] flex-grow items-center rounded-xl bg-lightPrimary text-xs text-gray-600">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Client, N° Facture, ..."
                                value={search}
                                onChange={(e) =>
                                    onFilter(
                                        e.target.value || '',
                                        status,
                                        paiementMethod,
                                        account,
                                    )
                                }
                                className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                            />
                        </div>
                    </div>
                    <div className="xl:ml-5 lg:mt-1 col-span-2">
                        <p className="text-xs ml-1">Statut</p>
                        <Select
                            extra="!mt-0 pt-[5px] h-[30px] !w-[200px]"
                            value={status}
                            options={[
                                { label: 'Tous', value: 'all' },
                                { label: 'Non envoyée', value: 'generated' },
                                {
                                    label: 'Envoyée',
                                    value: 'sent',
                                },
                                { label: 'Payée', value: 'paid' },
                                { label: 'Avoir', value: 'credit-note' },
                                { label: 'Annulée', value: 'canceled' },
                            ]}
                            displayKey="label"
                            onSelect={(e: any) => setStatus(e.value)}
                            required={false}
                        />
                    </div>
                    <div className="xl:ml-7 lg:mt-1 col-span-2">
                        <p className="text-xs ml-1">Réglée par</p>
                        <Select
                            extra="!mt-0 pt-[5px] h-[30px] !w-[200px]"
                            options={[
                                {
                                    value: 'all',
                                    label: 'Tous',
                                },
                                {
                                    value: 'cash',
                                    label: 'Espèces',
                                },
                                {
                                    value: 'check',
                                    label: 'Chèque',
                                },
                                {
                                    value: 'transfer',
                                    label: 'Virement',
                                },
                                {
                                    value: 'credit-card',
                                    label: 'Carte bancaire',
                                },
                                {
                                    value: 'direct-debit',
                                    label: 'Prélèvement',
                                },
                            ]}
                            displayKey="label"
                            onSelect={(e: any) => {
                                setPaymentMethod(e.value);
                            }}
                            required={false}
                        />
                    </div>
                    <div className="xl:ml-9 lg:mt-1 col-span-2">
                        <p className="text-xs ml-1">Commercial assigné</p>
                        <Select
                            extra="!mt-0 pt-[5px] h-[30px] !w-[200px]"
                            options={[
                                {
                                    value: 'all',
                                    label: 'Tous',
                                },
                                ...accounts.map((account) => ({
                                    value: account._id,
                                    label: account.fullName,
                                })),
                            ]}
                            displayKey="label"
                            onSelect={(e: any) => {
                                setAccount(e.value);
                            }}
                            required={false}
                        />
                    </div>
                </div>

                <div className="mt-4 overflow-x-scroll xl:overflow-x-hidden">
                    <table className="w-full">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    className="!border-px !border-gray-400"
                                >
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                onClick={header.column.getToggleSortingHandler()}
                                                className={`cursor-pointer border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30 ${header.column.id === 'select' ? 'w-[10px]' : ''}`}
                                            >
                                                <div className="items-center justify-between text-xs text-gray-200">
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                    {{
                                                        asc: '',
                                                        desc: '',
                                                    }[
                                                        header.column.getIsSorted() as string
                                                    ] ?? null}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className={`${cell.column.id === 'select' ? 'w-[10px]' : 'min-w-[150px]'} border-white/0 py-3  pr-4`}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {table.getRowModel().rows.length === 0 && (
                        <div className="col-span-12 mt-10">
                            <img
                                className="mx-auto flex h-[125px] w-[125px] items-center justify-center"
                                src={invoiceImg}
                                alt=""
                            />
                            <h4 className="col-span-12 my-auto mt-3 text-center text-2xl font-medium text-navy-700 dark:text-white">
                                Aucune facture disponible
                            </h4>
                            <p className="col-span-12 my-auto mt-3 text-center text-base text-navy-700 dark:text-white">
                                Les filtres appliqués ne correspondent à aucune
                                facture exitante.
                            </p>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
}
